import { useEffect, useState } from "react";
import type { Authorization, DayMailStatus, Promotions } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { Dialog } from "../../ui/Dialog";
import type { MailScheme } from "../orders/mail-dom";
import { MailPreview } from "../orders/MailPreview";

interface Props {
  /** The Beheer code: whoever may save the actions may send the mail. */
  authorization: Authorization;
  /** The day promotion: the status is asked again when it changes. */
  day: Promotions["day"];
}

const SCHEMES: readonly MailScheme[] = ["light", "dark"];

/** Why the button is off, in words; null while it can be used. */
export function dayMailReason(status: DayMailStatus): string | null {
  const reasons = t.promotions.dayMail.reasons;
  switch (status.reason) {
    case null:
      return null;
    case "sent":
      return reasons.sent(status.sent?.recipients ?? 0);
    default:
      return reasons[status.reason];
  }
}

/**
 * "Mail de dagpromo naar je klanten" (Furkan, 14/10/2026), under the Dagpromo in Beheer › Acties: on a day of
 * the action, once a day, a mail to the shop's customers who want its actions by mail. The server decides
 * whether it may (GET /device/promotions/day-mail); the till asks to confirm with the number of customers.
 * "Voorbeeld bekijken" shows the mail as a customer gets it, also on another day.
 */
export function DayMailButton({ authorization, day }: Props) {
  const { api, report, toast } = useTill();
  const text = t.promotions.dayMail;
  const [status, setStatus] = useState<DayMailStatus | null>(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [scheme, setScheme] = useState<MailScheme>(() =>
    document.documentElement.dataset.theme === "dark" ? "dark" : "light",
  );
  const dayKey = JSON.stringify(day);

  useEffect(() => {
    const controller = new AbortController();
    api
      .request<{ status: DayMailStatus }>("GET", "/device/promotions/day-mail", {
        retry: true,
        signal: controller.signal,
      })
      .then((answer) => {
        setStatus(answer.status);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) report(error);
      });
    return () => {
      controller.abort();
    };
  }, [api, report, dayKey]);

  const send = async () => {
    setBusy(true);
    try {
      const answer = await api.request<{ recipients: number }>("POST", "/device/promotions/day-mail", {
        staffToken: authorization.token,
      });
      toast(text.sent(answer.recipients));
      setStatus(
        (before) =>
          before && {
            ...before,
            canSend: false,
            reason: "sent",
            sent: { recipients: answer.recipients, at: new Date().toISOString(), by: "" },
          },
      );
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  };

  const showPreview = async () => {
    try {
      setPreview(await api.request<{ subject: string; html: string }>("GET", "/device/promotions/day-mail/preview"));
    } catch (error) {
      report(error);
    }
  };

  const reason = status === null ? null : dayMailReason(status);
  return (
    <div className="daymail">
      <button
        type="button"
        className="btn small"
        disabled={busy || status?.canSend !== true}
        onClick={() => {
          setAsking(true);
        }}
      >
        {text.button}
      </button>
      <button type="button" className="btn small" onClick={() => void showPreview()}>
        {text.preview}
      </button>
      {status !== null && <span className="note">{reason ?? text.ready(status.recipients)}</span>}
      {asking && status !== null && (
        <ConfirmDialog
          message={text.confirm(status.recipients)}
          confirm={text.send}
          tone="primary"
          onConfirm={() => void send()}
          onClose={() => {
            setAsking(false);
          }}
        />
      )}
      {preview !== null && (
        <Dialog
          title={text.previewTitle}
          subtitle={preview.subject}
          onClose={() => {
            setPreview(null);
          }}
          actions={
            <div className="seg" role="group" aria-label={t.orders.mails.version}>
              {SCHEMES.map((option) => (
                <button
                  type="button"
                  key={option}
                  aria-pressed={scheme === option}
                  onClick={() => {
                    setScheme(option);
                  }}
                >
                  {t.orders.mails.versions[option]}
                </button>
              ))}
            </div>
          }
        >
          <MailPreview html={preview.html} scheme={scheme} />
        </Dialog>
      )}
    </div>
  );
}
